import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import TopTopicsBlock from '../components/trending/TopTopicsBlock';
import HeroImageSlideshow from '../components/layout/HeroImageSlideshow';
import { usePageSeo } from '../hooks/usePageSeo';
import { ALL_TOOLS, toolToTrendingItem } from '../lib/toolsCatalog';
import { SIMULATOR_CATALOG, simulatorToTrendingItem } from '../lib/simulationCatalog';
import { useHowItWorksTopics, topicToTrendingItem } from '../lib/howItWorksHierarchy';
import { useArticleSections, articleToTrendingItem } from '../lib/articlesHierarchy';

const HOME_TOP_N = 6;

// Same as the site-wide defaults in public/index.html; set here too so returning to Home
// from a page with its own title always shows these.
const PAGE_TITLE = 'AUTOXIUM | Automotive Tools, Simulations & Concepts';
const PAGE_DESCRIPTION =
  'Explore automotive engineering concepts, interactive simulations, engineering tools, technical articles, and practical resources.';

// One Home block: heading linking to the section's own listing page, an item count, and
// that section's top HOME_TOP_N trending items (most recently added when the section
// has no visits yet), with a "More" link when the section has more items than fit.
const HomeSectionBlock = ({ id, title, to, noun, sectionType, items, loading, blockTitle, moreLabel }) => (
  <div data-testid={`home-section-${id}`}>
    <div className="flex items-center justify-between mb-4">
      <Link to={to} className="group">
        <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight group-hover:text-autoxium-blue transition-colors">
          {title}
        </h2>
      </Link>
      {!loading && (
        <p className="font-mono text-sm text-muted-foreground">
          {items.length} {noun}{items.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
    {loading ? (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    ) : (
      <TopTopicsBlock
        sectionType={sectionType}
        items={items}
        hasMore={items.length > HOME_TOP_N}
        moreTopicsPath={to}
        testId={`home-${id}`}
        size="sm"
        topN={HOME_TOP_N}
        title={blockTitle}
        moreLabel={moreLabel}
      />
    )}
  </div>
);

const HomePage = () => {
  usePageSeo(PAGE_TITLE, PAGE_DESCRIPTION);
  const { topics, loading: topicsLoading } = useHowItWorksTopics();
  const { articles, loading: articlesLoading } = useArticleSections();

  const toolItems = useMemo(() => ALL_TOOLS.map(toolToTrendingItem), []);
  const simulatorItems = useMemo(() => SIMULATOR_CATALOG.map(simulatorToTrendingItem), []);
  const topicItems = useMemo(() => topics.map(topicToTrendingItem), [topics]);
  const articleItems = useMemo(() => articles.map(articleToTrendingItem), [articles]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="bg-chassis text-white py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6">
              <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
                Automotive Knowledge Platform
              </p>
              <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                Automotive Simulations, Tools &amp; Concepts
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Explore automotive engineering concepts, interactive simulations, engineering tools, technical articles, and practical resources for automotive engineers.
              </p>
            </div>
            <div className="md:col-span-6 hidden md:block self-stretch">
              <HeroImageSlideshow alt="Automotive Simulations, Tools & Concepts" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 space-y-12">
          <HomeSectionBlock
            id="tools"
            title="Tools"
            to="/tools"
            noun="tool"
            sectionType="tool"
            items={toolItems}
            blockTitle="Top Tools"
            moreLabel="More Tools"
          />
          <HomeSectionBlock
            id="simulation"
            title="Simulation"
            to="/simulation"
            noun="simulator"
            sectionType="simulation"
            items={simulatorItems}
            blockTitle="Top Simulators"
            moreLabel="More Simulators"
          />
          <HomeSectionBlock
            id="how-it-works"
            title="How Does It Work?"
            to="/how-it-works"
            noun="topic"
            sectionType="how_it_works"
            items={topicItems}
            loading={topicsLoading}
            blockTitle="Top Topics"
            moreLabel="More Topics"
          />
          <HomeSectionBlock
            id="articles"
            title="Articles"
            to="/articles"
            noun="article"
            sectionType="article"
            items={articleItems}
            loading={articlesLoading}
            blockTitle="Top Articles"
            moreLabel="More Articles"
          />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
